import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  SimpleGrid, Text,
  Alert,
  Skeleton,
  Box
} from '@mantine/core';
import {
  IconAlertCircle,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { reportsApi } from '../../api/reports';
import { QuarterlyReportCard } from '../reports/QuarterlyReportCard';
import { MilestoneStatusCard } from './MilestoneStatusCard';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Milestone, MilestoneStatus, Activity } from '../../types/milestones';
import { milestonesApi } from '../../api/milestones';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { SectionHeader } from '../common/SectionHeader';
import { PageHeader } from '../common/PageHeader';

import { useGroupQuery } from '../../hooks/useGroupQuery';
import { useGroup } from '../../contexts/GroupContext';
import { EmptyReportsState } from '../reports/EmptyReportsState';
import { showApiErrorNotification } from '../../utils/notifications';
import { SafeTPerformanceMetrics } from './safeT/SafeTPerformanceMetrics';
import { BlackTechStreetPerformanceMetrics } from './blackTechStreet/BlackTechStreetPerformanceMetrics';
import { ThecPerformanceMetrics } from './thec/ThecPerformanceMetrics';
import { AtlasPerformanceMetrics } from './atlas/AtlasPerformanceMetrics';
import { TccPerformanceMetrics } from './tcc/TccPerformanceMetrics';
import { TuAswpPerformanceMetrics } from './tuAswp/TuAswpPerformanceMetrics';
import { InTulsaAswpPerformanceMetrics } from './inTulsaAswp/InTulsaAswpPerformanceMetrics';


// Home dashboard is always the standard (subawardee) view; admins use `/admin/dashboard`.
export function Dashboard() {
  return <StandardDashboard />;
}

// The actual standard dashboard component
function StandardDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedGroup } = useGroup();
  
  // Separate queries for different data
  // Using useGroupQuery to include selectedGroup in the query keys

  const {
    data: reports,
    isLoading: isReportsLoading,
    error: reportsError
  } = useGroupQuery(
    ['recent-reports'],
    reportsApi.getQuarterlyReports,
  );



  const {
    data: upcomingMilestonesData,
    isLoading: isMilestonesLoading,
    error: milestonesError
  } = useGroupQuery(
    ['upcomingMilestones'],
    milestonesApi.getUpcomingMilestones,
  );

  // Full milestones data for getting activity context
  const {
    data: fullMilestonesData,
    error: fullMilestonesError
  } = useGroupQuery(
    ['milestones'],
    milestonesApi.getMilestones,
  );

  // Milestone status update mutation
  const updateMilestoneStatusMutation = useMutation({
    mutationFn: ({ milestoneId, status, context }: { 
      milestoneId: string; 
      status: MilestoneStatus; 
      context: any 
    }) => milestonesApi.updateMilestoneStatus(milestoneId, status, context),
    onSuccess: () => {
      // Invalidate both milestone queries
      queryClient.invalidateQueries({ queryKey: ['milestones'] });
      queryClient.invalidateQueries({ queryKey: ['upcomingMilestones'] });
    },
    onError: (error) => {
      showApiErrorNotification(error, 'Failed to update milestone status');
    }
  });

  // State for upcoming milestones
  const [milestonesToShow, setMilestonesToShow] = useState<Milestone[]>([]);
  
  // Get all activities from the full milestones data
  const allActivities = useMemo(() => {
    if (!fullMilestonesData?.objectives) return [];
    
    const activities: Activity[] = [];
    fullMilestonesData.objectives.forEach(obj => {
      obj.key_activities.forEach(ka => {
        ka.goals.forEach(goal => {
          activities.push(...goal.activities);
        });
      });
    });
    
    return activities;
  }, [fullMilestonesData]);
  
  // We no longer use a combined loading state
  // Instead, individual sections will handle their own loading states

  // Handle API errors with notifications and console logging

  useEffect(() => {
    if (reportsError) {
      console.error("Error fetching reports:", reportsError);
      showApiErrorNotification(reportsError, 'Failed to load recent reports. Please try again later.');
    }
  }, [reportsError]);



  useEffect(() => {
    if (milestonesError) {
      console.error("Error fetching milestones:", milestonesError);
      showApiErrorNotification(milestonesError, 'Failed to load upcoming milestones. Please try again later.');
    }
  }, [milestonesError]);

  useEffect(() => {
    if (fullMilestonesError) {
      console.error("Error fetching full milestones:", fullMilestonesError);
      showApiErrorNotification(fullMilestonesError, 'Failed to load milestone details. Please try again later.');
    }
  }, [fullMilestonesError]);
  
  // Update milestones when data changes
  useEffect(() => {
    if (upcomingMilestonesData) {
      // The API returns upcoming milestones array, take the first 4
      const milestones = Array.isArray(upcomingMilestonesData) ? upcomingMilestonesData : [];
      const milestonesToDisplay = milestones.slice(0, 4);
      setMilestonesToShow(milestonesToDisplay);
    } else {
      setMilestonesToShow([]);
    }
  }, [upcomingMilestonesData]);

  // Filter reports to show only relevant ones
  // const currentDate = dayjs();
  const reportsToShow = useMemo(
    () =>
      reports
        ? [...reports].sort((a, b) => dayjs(a.dueDate).valueOf() - dayjs(b.dueDate).valueOf())
        : [],
    [reports],
  );
  const reportsWithKeys = useMemo(() => {
    const seen = new Map<string, number>();
    return reportsToShow.map((report) => {
      const baseKey = [report.id, report.dueDate, report.title].join(':');
      const count = seen.get(baseKey) ?? 0;
      seen.set(baseKey, count + 1);
      return {
        report,
        key: count === 0 ? baseKey : `${baseKey}::${count}`,
      };
    });
  }, [reportsToShow]);
  
  // Function to get activity title for a milestone
  const getActivityTitle = (milestone: Milestone): string => {
    // For upcoming milestones from API, we might not have the full nested structure
    // so we can't easily get the activity title. Return a generic title or empty string.
    if (milestone.activity_id && !allActivities.length) {
      return `Activity ${milestone.activity_id}`;
    }
    
    const activity = allActivities.find(a => a.milestones.some(m => m.id === milestone.id));
    return activity ? activity.title : '';
  };
  const milestonesWithKeys = useMemo(() => {
    const seen = new Map<string, number>();
    return milestonesToShow.map((milestone) => {
      const baseKey = [
        milestone.id,
        milestone.objective_id,
        milestone.key_activity_id,
        milestone.goal_id,
        milestone.activity_id,
        milestone.due_date,
      ].join(':');
      const count = seen.get(baseKey) ?? 0;
      seen.set(baseKey, count + 1);
      return {
        milestone,
        key: count === 0 ? baseKey : `${baseKey}::${count}`,
      };
    });
  }, [milestonesToShow]);

  const handleSubmit = async (reportId: string) => {
    try {

      navigate(`/reports/${reportId}/submit`);
    } catch (error) {
      notifications.hide('loading-report');
      notifications.show({
        title: 'Error',
        message: 'Failed to load report. Please try again.',
        color: 'red',
      });
      console.error('Error loading report:', error);
    }
  };

  const handleView = (reportId: string): void => {
    navigate(`/reports/${reportId}`);
  };

  const handleUpdate = (reportId: string): void => {
    navigate(`/reports/${reportId}`);
  };

  const handleViewAll = (): void => {
    navigate('/reports');
  };

  const handleViewAllMilestones = (): void => {
    navigate('/milestones');
  };

  // Handle milestone toggle - useGroupQuery will automatically refetch when needed
  const handleToggleMilestone = async (compositeId: string, status: MilestoneStatus): Promise<void> => {
    // Parse the composite ID: "milestoneId:activityId:goalId:keyActivityId:objectiveId"
    const [milestoneId, activityId, goalId, keyActivityId, objectiveId] = compositeId.split(':');
    
    await updateMilestoneStatusMutation.mutateAsync({
      milestoneId,
      status,
      context: {
        objective_id: objectiveId,
        key_activity_id: keyActivityId,
        goal_id: goalId,
        activity_id: activityId
      }
    });
  };

  const showOpenReportsSection = reportsError || isReportsLoading || reportsToShow.length > 0;
  const showUpcomingMilestonesSection = milestonesError || isMilestonesLoading || milestonesToShow.length > 0;
  const showSafeTMetrics = !!selectedGroup && ['Skyway Range', 'DronePort Network', 'TU - SAFE-T'].includes(selectedGroup);
  const showBlackTechStreetMetrics = selectedGroup === 'Black Tech Street';
  const showThecMetrics = selectedGroup === 'THEC';
  const showTuAswpMetrics = selectedGroup === 'TU - ASWP';
  const showAtlasMetrics = selectedGroup === 'Atlas';
  const showTccMetrics = selectedGroup === 'TCC';
  const showInTulsaAswpMetrics = selectedGroup === 'inTulsa - ASWP';
  const showAnyPerformanceMetrics = showSafeTMetrics || showBlackTechStreetMetrics || showThecMetrics || showTuAswpMetrics || showAtlasMetrics || showTccMetrics || showInTulsaAswpMetrics;
  const showNoImmediateActionItems = !showOpenReportsSection && !showUpcomingMilestonesSection && !showAnyPerformanceMetrics;

  return (
    <Container size="xl" mt="xl" pb="xl" role="main" aria-label="Dashboard" px={0}>
      <PageHeader 
        title="Dashboard" 
        subtitle="Monitor report submissions, track performance metrics, and manage quarterly reporting requirements."
      />
      
      {/* Open Reports Section - hide when no data (exclude API failure) */}
      {showOpenReportsSection ? (
      <Box mb={60}>
        <SectionHeader
          title="Open Reports"
          action={{
            label: "View All",
            onClick: handleViewAll,
            ariaLabel: "View all reports"
          }}
        />

        {reportsError ? (
          <Alert 
            icon={<IconAlertCircle size={16} />} 
            title="Error" 
            color="red"
            mb="xl"
            role="alert"
          >
            Failed to load reports. Please try again later.
          </Alert>
        ) : null}

        <SimpleGrid
          cols={4}
          spacing="lg"
          breakpoints={[
            { maxWidth: 1400, cols: 3 },
            { maxWidth: 1024, cols: 2 },
            { maxWidth: 768, cols: 1 },
            { maxWidth: 480, cols: 1, spacing: 'md' }
          ]}
        >
          {isReportsLoading ? (
            ['reports-skeleton-1', 'reports-skeleton-2', 'reports-skeleton-3', 'reports-skeleton-4'].map((skeletonKey) => (
              <Skeleton key={skeletonKey} height={180} radius="md" animate={true} />
            ))
          ) : reportsError ? null : reportsToShow.length > 0 ? (
            reportsWithKeys.map(({ report, key }) => (
              <QuarterlyReportCard
                key={key}
                report={report}
                onSubmit={handleSubmit}
                onView={handleView}
                onUpdate={handleUpdate}
              />
            ))
          ) : (
            <Box style={{ gridColumn: '1 / -1' }}>
              <EmptyReportsState />
            </Box>
          )}
        </SimpleGrid>
      </Box>
      ) : null}
      
      {/* Upcoming Milestones Section - hide when no data (exclude API failure) */}
      {showUpcomingMilestonesSection ? (
      <Box mb={60}>
        <SectionHeader
          title="Upcoming Milestones"
          action={{
            label: "View All",
            onClick: handleViewAllMilestones,
            ariaLabel: "View all milestones"
          }}
        />

        {milestonesError ? (
          <Alert 
            icon={<IconAlertCircle size={16} />} 
            title="Error" 
            color="red"
            mb="xl"
            role="alert"
          >
            Failed to load upcoming milestones. Please try again later.
          </Alert>
        ) : null}
        
        <SimpleGrid
          cols={4}
          spacing="lg"
          breakpoints={[
            { maxWidth: 1400, cols: 3 },
            { maxWidth: 1024, cols: 2 },
            { maxWidth: 768, cols: 1 },
            { maxWidth: 480, cols: 1, spacing: 'md' }
          ]}
        >
          {isMilestonesLoading ? (
            ['milestones-skeleton-1', 'milestones-skeleton-2', 'milestones-skeleton-3', 'milestones-skeleton-4'].map((skeletonKey) => (
              <Skeleton key={skeletonKey} height={180} radius="md" animate={true} />
            ))
          ) : milestonesError ? null : milestonesToShow.length > 0 ? (
            milestonesWithKeys.map(({ milestone, key }) => (
              <MilestoneStatusCard
                key={key}
                milestone={milestone}
                activityTitle={getActivityTitle(milestone)}
                onToggle={handleToggleMilestone}
              />
            ))
          ) : (
            <Text color="dimmed" style={{ gridColumn: '1 / -1' }}>
              No upcoming milestones found. View all milestones to see your complete list.
            </Text>
          )}
        </SimpleGrid>
      </Box>
      ) : null}

      {/* Performance Metrics Sections */}
      {showSafeTMetrics ? (
        <Box mb={60}>
          <SectionHeader title="Performance Metrics" />
          <SafeTPerformanceMetrics selectedGroup={selectedGroup} />
        </Box>
      ) : null}

      {showBlackTechStreetMetrics ? (
        <Box mb={60}>
          <SectionHeader title="Performance Metrics" />
          <BlackTechStreetPerformanceMetrics />
        </Box>
      ) : null}

      {showThecMetrics ? (
        <Box mb={60}>
          <SectionHeader title="Performance Metrics" />
          <ThecPerformanceMetrics />
        </Box>
      ) : null}

      {showTuAswpMetrics ? (
        <Box mb={60}>
          <SectionHeader title="Performance Metrics" />
          <TuAswpPerformanceMetrics />
        </Box>
      ) : null}

      {showAtlasMetrics ? (
        <Box mb={60}>
          <SectionHeader title="Performance Metrics" />
          <AtlasPerformanceMetrics />
        </Box>
      ) : null}

      {showTccMetrics ? (
        <Box mb={60}>
          <SectionHeader title="Performance Metrics" />
          <TccPerformanceMetrics />
        </Box>
      ) : null}

      {showInTulsaAswpMetrics ? (
        <Box mb={60}>
          <SectionHeader title="Performance Metrics" />
          <InTulsaAswpPerformanceMetrics selectedGroup={selectedGroup} />
        </Box>
      ) : null}

      {showNoImmediateActionItems ? (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="No immediate action items"
          color="blue"
          variant="light"
          role="status"
        >
          There are no immediate action items to review right now. Check back later for new reports, milestones, or performance updates.
        </Alert>
      ) : null}
    </Container>
  );
} 
